import { useMemo } from 'react';
import { Basis } from '~/models/studio';
import { useCurrentFrame } from '~/systems/currentFrame';
import { useHelpRef } from '~/systems/help';
import { pickStateNames, useUiState } from '~/systems/uiState';
import { mapRange } from '~/util';
import { BOTTOM_LEGEND_SIZE, LEFT_LEGEND_SIZE } from '~/util/registerData';
import type { Atom } from '~/models/studio';
import type { Vec2 } from '~/util';
import type { PulseDetails, Viewport } from '~/util/registerData';
import './AtomLegend.css';

interface AtomLegendProps {
  atom: Atom | null;
  atomIdx: number | null;
  pulseDetails: PulseDetails | null;
  viewport: Viewport;
}

const STATE_LEGEND_CONF = {
  height: 200,
  width: 200,
  offset: { left: 20, top: 10 },
  opacity: { min: 0.1, max: 1 },
  center: { x: 70, y: 165 },
  orbitRadius: [20, 60, 150],
  orbitXInterval: 30,
  electronRadius: 5
};

function generatePathArcFromX(
  center: Vec2,
  radius: number,
  x: [number, number]
) {
  const radiusSquared = radius * radius;
  const [x1, x2] = x;
  const y1 =
    -Math.sqrt(radiusSquared - (center.x - x1) * (center.x - x1)) + center.y;
  const y2 =
    -Math.sqrt(radiusSquared - (center.x - x2) * (center.x - x2)) + center.y;
  return `M ${x2} ${y2} A ${radius} ${radius} 0 0 0 ${x1} ${y1}`;
}

export default function AtomLegend({
  atom,
  atomIdx,
  pulseDetails,
  viewport
}: AtomLegendProps) {
  const uiState = useUiState();
  const currentFrame = useCurrentFrame();
  const helpRef = useHelpRef<SVGSVGElement>('state-legend');

  const stateLegendData = useMemo(() => {
    if (atom == null || atomIdx == null) {
      return null;
    }
    const state = currentFrame.averageState[atomIdx];
    if (state == null) {
      return null;
    }

    const sample = pulseDetails?.sample ?? 0;
    const basis = pulseDetails?.channel.basis;
    const maxAmp = pulseDetails?.channel.maxAmp ?? 1;
    const scaledSample = Math.sqrt(Math.max(sample / maxAmp, 0));

    const opacity = STATE_LEGEND_CONF.opacity;
    const transitionOpacity = mapRange(
      scaledSample,
      0,
      1,
      opacity.min,
      opacity.max
    );
    return {
      orbitOpacity: {
        ground: mapRange(Math.sqrt(state[0]), 0, 1, opacity.min, opacity.max),
        hyperfine: mapRange(
          Math.sqrt(state[1]),
          0,
          1,
          opacity.min,
          opacity.max
        ),
        rydberg: mapRange(Math.sqrt(state[2]), 0, 1, opacity.min, opacity.max)
      },
      basisOpacity: {
        digital: basis === Basis.Digital ? transitionOpacity : opacity.min,
        groundRydberg:
          basis === Basis.GroundRydberg ? transitionOpacity : opacity.min
      },
      basisRepr: pickStateNames(uiState.basisRepr),
      probability: [
        Math.round(state[0] * 100),
        Math.round(state[1] * 100),
        Math.round(state[2] * 100)
      ]
    };
  }, [
    atom,
    atomIdx,
    currentFrame.averageState,
    pulseDetails?.sample,
    pulseDetails?.channel.basis,
    pulseDetails?.channel.maxAmp,
    uiState.basisRepr
  ]);

  const stateLegendPos = useMemo(() => {
    if (atom == null) {
      return { top: 0, left: 0 };
    }
    const vp = viewport;
    const top =
      (atom.pos.y - vp.center.y) * -vp.scale +
      vp.availableHeight / 2 +
      BOTTOM_LEGEND_SIZE +
      STATE_LEGEND_CONF.offset.top;
    const left =
      (atom.pos.x - vp.center.x) * vp.scale +
      vp.availableWidth / 2 +
      LEFT_LEGEND_SIZE +
      STATE_LEGEND_CONF.offset.left;
    return { top, left };
  }, [atom, viewport]);

  if (stateLegendData == null) {
    return null;
  }

  const c = STATE_LEGEND_CONF.center;
  const r = STATE_LEGEND_CONF.orbitRadius;
  const xi = STATE_LEGEND_CONF.orbitXInterval;
  const er = STATE_LEGEND_CONF.electronRadius;

  return (
    <svg
      ref={helpRef}
      height={STATE_LEGEND_CONF.height}
      width={STATE_LEGEND_CONF.width}
      className="AtomLegend"
      style={{
        top: `${stateLegendPos.top}px`,
        left: `${stateLegendPos.left}px`
      }}
    >
      <circle cx={c.x} cy={c.y} r={10} className="AtomLegend-atom" />
      <g className="AtomLegend-stateLegendOrbits">
        <g
          className="AtomLegend-hyperfine"
          opacity={stateLegendData.orbitOpacity.hyperfine}
        >
          <circle cx={c.x} cy={c.y - r[0]} r={er} stroke="none" />
          <circle cx={c.x} cy={c.y} r={r[0]} fill="none" />
        </g>
        <g
          className="AtomLegend-ground"
          opacity={stateLegendData.orbitOpacity.ground}
        >
          <circle cx={c.x} cy={c.y - r[1]} r={er} stroke="none" />
          <path
            d={generatePathArcFromX(c, r[1], [c.x - xi, c.x + xi])}
            fill="none"
          />
        </g>
        <g
          className="AtomLegend-rydberg"
          opacity={stateLegendData.orbitOpacity.rydberg}
        >
          <circle cx={c.x} cy={c.y - r[2]} r={er} stroke="none" />
          <path
            d={generatePathArcFromX(c, r[2], [c.x - xi, c.x + xi])}
            fill="none"
          />
        </g>
      </g>
      <g className="AtomLegend-lines">
        <g
          className="AtomLegend-hyperfineToGround"
          opacity={stateLegendData.basisOpacity.digital}
        >
          <line
            x1={c.x}
            x2={c.x}
            y1={c.y - r[0] - er - 6}
            y2={c.y - r[1] + er + 6}
            strokeWidth="4"
          />
          <polygon
            points={`${c.x - 5},${c.y - r[1] + er + 10} ${c.x},${c.y - r[1] + er + 3} ${c.x + 5},${c.y - r[1] + er + 10}`}
            strokeWidth="1"
          />
          <polygon
            points={`${c.x - 5},${c.y - r[0] - er - 10} ${c.x},${c.y - r[0] - er - 3} ${c.x + 5},${c.y - r[0] - er - 10}`}
            strokeWidth="1"
          />
        </g>
        <g
          className="AtomLegend-groundToRydberg"
          opacity={stateLegendData.basisOpacity.groundRydberg}
        >
          <line
            x1={c.x}
            x2={c.x}
            y1={c.y - r[1] - er - 6}
            y2={c.y - r[2] + er + 6}
            strokeWidth="4"
          />
          <polygon
            points={`${c.x - 5},${c.y - r[2] + er + 10} ${c.x},${c.y - r[2] + er + 3} ${c.x + 5},${c.y - r[2] + er + 10}`}
            strokeWidth="1"
          />
          <polygon
            points={`${c.x - 5},${c.y - r[1] - er - 10} ${c.x},${c.y - r[1] - er - 3} ${c.x + 5},${c.y - r[1] - er - 10}`}
            strokeWidth="1"
          />
        </g>
      </g>
      <g className="AtomLegend-labels">
        <g className="AtomLegend-hyperfine">
          <text
            x={c.x - xi - 5}
            y={c.y - r[0]}
            dominantBaseline="central"
            textAnchor="end"
          >
            {stateLegendData.probability[1]}%
          </text>
          <text x={c.x + xi + 7} y={c.y - r[0]} dominantBaseline="central">
            ❘{stateLegendData.basisRepr[1]}⟩ Hyperfine
          </text>
        </g>
        <g className="AtomLegend-ground">
          <text
            x={c.x - xi - 5}
            y={c.y - r[1]}
            dominantBaseline="central"
            textAnchor="end"
          >
            {stateLegendData.probability[0]}%
          </text>
          <text x={c.x + xi + 7} y={c.y - r[1]} dominantBaseline="central">
            ❘{stateLegendData.basisRepr[0]}⟩ Ground
          </text>
        </g>
        <g className="AtomLegend-rydberg">
          <text
            x={c.x - xi - 5}
            y={c.y - r[2]}
            dominantBaseline="central"
            textAnchor="end"
          >
            {stateLegendData.probability[2]}%
          </text>
          <text x={c.x + xi + 7} y={c.y - r[2]} dominantBaseline="central">
            ❘{stateLegendData.basisRepr[2]}⟩ Rydberg
          </text>
        </g>
      </g>
    </svg>
  );
}
